import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import { FaFileSignature } from 'react-icons/fa';
import { toast } from '../../toasts';
import esignatureService from '../../services/esignature.service';
import { getObjectServiceConfig } from '../../constants/objectServiceConfig';
import { Button, FieldLabel, FormField, Select, controlClass } from '../EntityList';
import { useLabels } from '../../hooks/useLabels';
import { getError } from '../../utils';

// Best-effort counterparty email prefill per entity type. Always editable —
// this is a convenience, never a requirement.
const resolveCounterparty = (objectType, object) => {
	switch (objectType) {
	case 'contract':
		return { name: object?.owner?.name || '', email: object?.owner?.email || '' };
	case 'assetSale':
		return { name: object?.purchaser?.name || '', email: object?.purchaser?.email || '' };
	case 'escritura':
		return { name: object?.owner?.name || '', email: object?.owner?.email || '' };
	case 'account':
		return { name: object?.name || '', email: object?.email || object?.mainContact?.email || '' };
	case 'contact':
		return { name: object?.name || '', email: object?.email || '' };
	default:
		return { name: '', email: '' };
	}
};

const STEPS = { TEMPLATE: 'template', SIGNERS: 'signers' };

/**
 * "Enviar a firmar" wizard. Step 1 offers any template already set up in
 * DocuSeal (e.g. one an admin cloned+replaced there for this exact document,
 * or reused as-is) alongside a "Crear plantilla nueva" fallback for when none
 * fits — free-tier DocuSeal has no API to create a template from a PDF, so
 * that fallback opens DocuSeal's own upload/field-editor UI via a deep link
 * (the backend pre-stamps signature fields onto the PDF, so it's usually
 * just "open, confirm, save") and refreshes the list in place. Step 2
 * collects one signer per the selected template's role.
 */
export default function SendForSignatureModal({ show, onHide, document, objectType, objectId, onSent }) {
	const { t } = useLabels();
	const [step, setStep] = useState(STEPS.TEMPLATE);
	const [preparing, setPreparing] = useState(false);
	const [deepLink, setDeepLink] = useState(null);
	const [templates, setTemplates] = useState([]);
	const [templatesLoading, setTemplatesLoading] = useState(false);
	const [selectedTemplateId, setSelectedTemplateId] = useState('');
	const [signers, setSigners] = useState([]);
	const [sending, setSending] = useState(false);
	const [signatureImageConfigured, setSignatureImageConfigured] = useState(false);

	useEffect(() => {
		if (!show) return;
		setStep(STEPS.TEMPLATE);
		setDeepLink(null);
		setSelectedTemplateId('');
		setSigners([]);
		loadTemplates();
	}, [show, document]);

	const handlePrepareNew = async () => {
		setPreparing(true);
		try {
			const config = getObjectServiceConfig(objectType);
			let counterpartyLabel = 'Contraparte';
			if (config) {
				try {
					const { data: object } = await config.service.getDetails(objectId);
					counterpartyLabel = resolveCounterparty(objectType, object).name || 'Contraparte';
				} catch (err) {
					// Non-blocking: role naming falls back to a generic label.
				}
			}
			const { data } = await esignatureService.prepare(document._id, [counterpartyLabel, 'Empresa']);
			setDeepLink(data.deepLink);
			window.open(data.deepLink, '_blank', 'noopener,noreferrer');
			await loadTemplates();
		} catch (err) {
			toast.error(getError(err));
		}
		setPreparing(false);
	};

	const loadTemplates = async () => {
		setTemplatesLoading(true);
		try {
			const { data } = await esignatureService.listTemplates();
			const list = Array.isArray(data) ? data : data?.data || [];
			setTemplates(list);
		} catch (err) {
			toast.error(getError(err));
		}
		setTemplatesLoading(false);
	};

	const handleSelectTemplate = async (templateId) => {
		setSelectedTemplateId(templateId);
		if (!templateId) {
			setSigners([]);
			return;
		}
		try {
			const { data } = await esignatureService.getTemplate(templateId);
			const roles = (data.submitters || []).map((s) => s.name);

			const config = getObjectServiceConfig(objectType);
			let counterparty = { name: '', email: '' };
			if (config) {
				try {
					const { data: object } = await config.service.getDetails(objectId);
					counterparty = resolveCounterparty(objectType, object);
				} catch (err) {
					// Non-blocking.
				}
			}
			let defaultSigner = { name: '', email: '' };
			try {
				const { data: settings } = await esignatureService.getSettings();
				defaultSigner = { name: settings?.defaultSignerName || '', email: settings?.defaultSignerEmail || '' };
			} catch (err) {
				// Non-blocking.
			}
			try {
				const { data: status } = await esignatureService.getStatus();
				setSignatureImageConfigured(!!status?.signatureImageUrl);
			} catch (err) {
				// Non-blocking.
			}

			setSigners(
				roles.map((role, index) => ({
					role,
					name: index === 0 ? counterparty.name : defaultSigner.name,
					email: index === 0 ? counterparty.email : defaultSigner.email,
					isCompanySigner: index !== 0,
				}))
			);
			setStep(STEPS.SIGNERS);
		} catch (err) {
			toast.error(getError(err));
		}
	};

	const updateSigner = (index, field, value) => {
		setSigners((prev) => prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)));
	};

	const handleSend = async () => {
		const incomplete = signers.find((s) => !s.name.trim() || !s.email.trim());
		if (incomplete) {
			toast.error(t('esignature.modal.signersRequired'));
			return;
		}
		setSending(true);
		try {
			await esignatureService.send(document._id, {
				templateId: selectedTemplateId,
				entityType: objectType,
				entityId: objectId,
				submitters: signers,
			});
			toast.success(t('esignature.modal.sent'));
			onSent?.();
			onHide();
		} catch (err) {
			toast.error(getError(err));
		}
		setSending(false);
	};

	return (
		<Modal
			show={show}
			onHide={onHide}
			centered
			animation={false}
			backdrop={sending ? 'static' : true}
			keyboard={!sending}
			dialogClassName='tw:max-w-[720px]'
			contentClassName='entity-list-page el-modal tw:overflow-hidden tw:rounded-[14px] tw:border-0 tw:font-sans tw:text-[14px]/[1.45] tw:text-ink-0 tw:shadow-overlay'
		>
			<Modal.Header
				closeButton={!sending}
				className='tw:items-start tw:border-b tw:border-border tw:px-[22px] tw:pt-[20px] tw:pb-[14px]'
			>
				<div className='tw:flex tw:items-start tw:gap-3'>
					<span className='tw:mt-0.5 tw:flex tw:h-[38px] tw:w-[38px] tw:shrink-0 tw:items-center tw:justify-center tw:rounded-[9px] tw:bg-accent-soft tw:text-accent'>
						<FaFileSignature />
					</span>
					<div className='tw:min-w-0'>
						<Modal.Title className='tw:text-[17px]/[1.3] tw:font-bold tw:tracking-[-0.01em] tw:text-ink-0'>
							{t('esignature.modal.title')}
						</Modal.Title>
						{document?.name && (
							<p className='tw:mb-0 tw:mt-1 tw:truncate tw:text-[12.5px] tw:text-ink-2'>{document.name}</p>
						)}
					</div>
				</div>
			</Modal.Header>
			<Modal.Body className='tw:flex tw:flex-col tw:gap-4 tw:px-[22px] tw:py-[18px]'>
				{step === STEPS.TEMPLATE && (
					<>
						<p className='tw:m-0 tw:text-[13px] tw:text-ink-1'>{t('esignature.modal.selectOrCreateIntro')}</p>
						<FormField id='esignature-template' label={t('esignature.modal.selectTemplate')}>
							<Select
								id='esignature-template'
								options={templates.map((tpl) => ({ value: tpl.id, label: tpl.name }))}
								placeholder='—'
								value={selectedTemplateId}
								onChange={(e) => handleSelectTemplate(e.target.value)}
								disabled={templatesLoading}
							/>
						</FormField>
						<div className='tw:flex tw:flex-wrap tw:gap-2'>
							<Button size='sm' onClick={loadTemplates} disabled={templatesLoading}>
								{templatesLoading ? t('esignature.modal.loading') : t('esignature.modal.refreshTemplates')}
							</Button>
							<Button size='sm' onClick={handlePrepareNew} disabled={preparing}>
								{preparing ? <Spinner animation='border' size='sm' /> : t('esignature.modal.prepareButton')}
							</Button>
						</div>
						{deepLink && (
							<div className='tw:rounded-[7px] tw:border tw:border-accent tw:bg-accent-soft tw:px-4 tw:py-3 tw:text-[13px]'>
								<p className='tw:mb-2'>{t('esignature.modal.templateInstructions')}</p>
								<a
									href={deepLink}
									target='_blank'
									rel='noopener noreferrer'
									className='tw:font-medium tw:text-accent tw:underline'
								>
									{t('esignature.modal.reopenLink')}
								</a>
							</div>
						)}
					</>
				)}

				{step === STEPS.SIGNERS && (
					<>
						<p className='tw:m-0 tw:text-[13px] tw:text-ink-1'>{t('esignature.modal.signersDescription')}</p>
						<div className='tw:flex tw:flex-col tw:gap-3'>
							{signers.map((signer, index) => (
								<div key={`${signer.role}-${index}`}>
									<div className='tw:grid tw:grid-cols-1 tw:items-end tw:gap-3 md:tw:grid-cols-[160px_1fr_1fr]'>
										<FieldLabel htmlFor={`signer-name-${index}`}>{signer.role}</FieldLabel>
										<Form.Control
											id={`signer-name-${index}`}
											className={controlClass}
											placeholder={t('concept.field.name')}
											value={signer.name}
											onChange={(e) => updateSigner(index, 'name', e.target.value)}
										/>
										<Form.Control
											id={`signer-email-${index}`}
											type='email'
											className={controlClass}
											placeholder='email@ejemplo.com'
											value={signer.email}
											onChange={(e) => updateSigner(index, 'email', e.target.value)}
										/>
									</div>
									{signer.isCompanySigner && signatureImageConfigured && (
										<p className='tw:mt-1.5 tw:mb-0 tw:text-[12px] tw:text-ok'>
											{t('esignature.modal.autoSignedHint')}
										</p>
									)}
								</div>
							))}
						</div>
						<div>
							<Button size='sm' variant='ghost' onClick={() => setStep(STEPS.TEMPLATE)}>
								{t('action.back')}
							</Button>
						</div>
					</>
				)}
			</Modal.Body>
			<Modal.Footer className='tw:justify-end tw:gap-2 tw:border-t tw:border-border tw:bg-surface-2 tw:px-[22px] tw:py-[14px]'>
				<Button variant='default' onClick={onHide} disabled={sending}>
					{t('action.cancel')}
				</Button>
				{step === STEPS.SIGNERS && (
					<Button variant='primary' onClick={handleSend} disabled={sending}>
						{sending ? <Spinner animation='border' size='sm' /> : t('esignature.send')}
					</Button>
				)}
			</Modal.Footer>
		</Modal>
	);
}
